/**
 * InvoiceDetailPage
 *
 * Dedicated view for a single invoice — opens from the Invoices list
 * row and from the "Open invoice" button on InvoiceSummaryCard. Shows
 * the full invoice (header, void banner, line items, totals, payment
 * history) and exposes the same actions as the sidebar card.
 */

import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { Fragment, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { displayGrossCents, displayLineGrossCents } from '@/utils/money';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import PaymentIcon from '@mui/icons-material/Payment';
import BlockIcon from '@mui/icons-material/Block';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import OutboxIcon from '@mui/icons-material/Outbox';
import {
  useInvoiceDetail,
  useFinalizeInvoice,
  useDeliverInvoice,
  useRetryInvoiceDelivery,
  useRemindInvoice,
  openInvoicePdf,
  printInvoicePdf,
  useVoidInvoice,
  invoiceWorkOrderRef,
  isCompletedCustomerRefundStatus,
  type InvoiceStatus,
} from '@/hooks/useInvoices';
import { InvoiceDeliverySection } from './InvoiceDeliverySection';
import { SveaPaymentSection } from './SveaPaymentSection';
import { SveaRefundControls } from './SveaRefundControls';
import { useSveaInvoiceSettlement } from '@/hooks/useSveaSettlements';
import { einvoiceChoice, useMaventaReceivers } from '@/hooks/useMaventaIntegration';
import { INVOICE_READ_PERMISSIONS } from '@/lib/invoicePermissions';
import { CreditNoteDialog } from './CreditNoteDialog';
import { InvoiceConsolidationSection } from './InvoiceConsolidationSection';
import { useInvoiceConsolidation } from '@/hooks/useConsolidatedInvoices';
import {
  printConsolidatedInvoice,
  toConsolidatedPrintDocument,
} from '@/components/workorders/InvoicePrintView';
import {
  isCollectingInvoice,
  readConsolidationSnapshot,
  readLineBookingId,
  resolveConsolidationView,
} from '@/components/workorders/consolidation/presentation';
import { useNotify } from '@/hooks/useNotify';
import { useAuthStore } from '@/stores/authStore';
import { formatMissingFieldLabels, translateBillingError } from '@/lib/billingErrors';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { RecordPaymentDialog } from '@/components/workorders/RecordPaymentDialog';
import { PaymentHistorySection } from '@/components/workorders/PaymentHistorySection';
import { groupInvoiceLines } from '@/lib/invoiceLineGroups';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { missingDraftFinalizationFields, savedDraftValidationKey, sellerIbanFromSettings } from './draftInvoiceForm';
import { recordPaymentInitialMethod } from './invoicePaymentMethods';
import { formatDate, formatDateTime } from '@/utils/locale';


/** Format a (possibly fractional) quantity with a Finnish decimal comma, no trailing zeros. */
function formatQuantity(quantity: number): string {
  return (Math.round(quantity * 100) / 100).toString().replace('.', ',');
}

/** Format a VAT/discount percentage with a Finnish decimal comma, no trailing zeros. */
function formatPercent(percent: number): string {
  return (Math.round(percent * 100) / 100).toString().replace('.', ',');
}

const STATUS_COLORS: Record<InvoiceStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  sent: 'info',
  paid: 'success',
  partial: 'warning',
  overdue: 'error',
  void: 'default',
  refunded: 'default',
  credited: 'default',
};

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const finalize = useFinalizeInvoice();
  const deliverInvoice = useDeliverInvoice();
  const retryDelivery = useRetryInvoiceDelivery();
  const remindInvoice = useRemindInvoice();
  const voidInvoice = useVoidInvoice();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  // Invoice administration and checkout are separate least-privilege capabilities.
  const canManageInvoices = hasPermission('invoices.manage');
  const canTakePayments = hasPermission('payments.take') || canManageInvoices;
  const canReadSettlements = hasAnyPermission(INVOICE_READ_PERMISSIONS);
  const settlement = useSveaInvoiceSettlement(id, canReadSettlements);

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);

  const { data: invoice, isPending, isError } = useInvoiceDetail(id);
  const consolidationQuery = useInvoiceConsolidation(id);
  const { tenantSettings } = useTenantSettings();
  // E-invoice option for this invoice's bill-to company (#3071); a credit
  // note goes the same way as the invoice it credits (#3072).
  const einvoiceable = invoice?.invoiceType === 'invoice' || invoice?.invoiceType === 'credit_note';
  const receivers = useMaventaReceivers(
    invoice?.billingParty?.companyId ?? null,
    canManageInvoices && einvoiceable,
  );
  const einvoice = einvoiceChoice(receivers.data);

  if (isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('detailPage.notFound')}</Alert>
        <Button startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} onClick={() => navigate('/invoices')}>
          {t('detailPage.backToList')}
        </Button>
      </Box>
    );
  }

  const remainingCents = invoice.totalCents - invoice.paidAmountCents;
  const lineGroups = groupInvoiceLines(invoice.lineItems, t('print.otherLinesGroup'));
  const isOverdue =
    invoice.dueDate && new Date(invoice.dueDate) < new Date() &&
    invoice.status !== 'paid' && invoice.status !== 'void';

  const consolidationView = resolveConsolidationView({
    status: invoice.status,
    live: consolidationQuery.data ?? null,
    snapshot: readConsolidationSnapshot(invoice),
  });
  const collecting = isCollectingInvoice(invoice.status);

  const handlePrint = () => {
    if (consolidationView && consolidationView.rows.length > 0) {
      const printed = toConsolidatedPrintDocument({
        invoiceNumber: invoice.invoiceNumber,
        paymentReferenceNumber: invoice.paymentReferenceNumber,
        periodStart: consolidationView.periodStart,
        periodEnd: consolidationView.periodEnd,
        rows: consolidationView.rows,
        lines: invoice.lineItems.map((line) => ({
          bookingId: readLineBookingId(line),
          description: line.description,
          quantityLabel: `${formatQuantity(line.quantity)} ${tc(`units.${line.unit || 'piece'}`)}`,
          amountLabel: formatPrice(displayLineGrossCents(line, invoice.taxPercent)),
        })),
        labels: {
          title: t('print.title'),
          period: t('consolidation.printPeriod'),
          registration: t('consolidation.printRegistration'),
          date: t('consolidation.printDate'),
          workOrder: t('consolidation.printWorkOrder'),
          reference: t('consolidation.printReference'),
          amount: t('consolidation.printAmount'),
          vatSummary: t('consolidation.printVatSummary'),
          vatRate: t('consolidation.printVatRate'),
          net: t('consolidation.printNet'),
          tax: t('consolidation.printTax'),
          gross: t('consolidation.printGross'),
          paymentReference: t('consolidation.printPaymentReference'),
          attachmentHeading: t('consolidation.printAttachment', {
            number: invoice.invoiceNumber,
          }),
          description: t('consolidation.printDescription'),
          quantity: t('consolidation.printQuantity'),
          manualLines: t('consolidation.printManualLines'),
        },
        formatMoney: (cents) => formatPrice(cents),
        formatWhen: (value) => formatDate(value),
        formatRate: (percent) => (percent === null ? '—' : `${formatPercent(percent)} %`),
      });
      void printConsolidatedInvoice(printed).then((result) => {
        if (result.status === 'popup-blocked') {
          notify.error(tc('error'), t('consolidation.printBlocked'));
        }
      }).catch((err: unknown) => {
        notify.error(tc('error'), translateBillingError(err, t));
      });
      return;
    }
    void printInvoicePdf(invoice.id).then((result) => {
      if (result.status === 'left-open') notify.info(tc('print.pdfUseBrowserDialog'));
    }).catch((err: unknown) => {
      notify.error(tc('error'), translateBillingError(err, t));
    });
  };

  // The legal PDF exists only after finalization. A draft uses the browser
  // preview (#1036). A collecting consolidated invoice is not finalized (#3933).
  const isFinalized = invoice.status !== 'draft' && !collecting;

  // Finalize guard from the saved invoice: content is immutable (#1960), so the
  // saved lines, due date and total are all there is to validate (#3876).
  const missingFieldsLabel = formatMissingFieldLabels(
    missingDraftFinalizationFields({
      recipientName: invoice.customerName,
      recipientEmail: invoice.customerEmail,
      sellerIban: sellerIbanFromSettings(
        tenantSettings == null ? undefined : { bankIban: tenantSettings['bankIban'] },
      ),
      totalCents: invoice.totalCents,
      requireEmail: true,
    }),
    t,
  );
  const savedValidationKey = savedDraftValidationKey(invoice);
  const finalizeBlockedReason = savedValidationKey
    ? t(savedValidationKey)
    : missingFieldsLabel !== ''
      ? t('edit.cannotFinalize', { fields: missingFieldsLabel })
      : null;
  const canFinalize = finalizeBlockedReason === null;
  // Receipts that were emailed (#3787); a receipt nobody was sent has no delivery to show.
  const receiptIds = invoice.payments.flatMap((payment) =>
    payment.receiptId && payment.receiptEmail ? [payment.receiptId] : [],
  );

  // A credit note applies to a finalized sales invoice that is not voided and
  // not already fully credited, and never to a credit note itself (#1037).
  const canCredit =
    invoice.invoiceType === 'invoice' &&
    isFinalized &&
    invoice.status !== 'void' &&
    invoice.status !== 'credited';

  const handleOpenPdf = async () => {
    try {
      await openInvoicePdf(invoice.id);
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  const handleDeliver = async (successKey = 'actions.deliverSuccess') => {
    try {
      await deliverInvoice.mutateAsync({ invoiceId: invoice.id, ...invoiceWorkOrderRef(invoice.bookingId) });
      notify.success(t(successKey));
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  const handleDeliverEinvoice = async () => {
    try {
      await deliverInvoice.mutateAsync({
        invoiceId: invoice.id,
        ...invoiceWorkOrderRef(invoice.bookingId),
        channel: 'einvoice',
        ...(einvoice.pick ? { recipientEia: einvoice.pick.eia, recipientOperator: einvoice.pick.operator } : {}),
      });
      notify.success(t('actions.deliverEinvoiceSuccess'));
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  const handleRemind = async () => {
    try {
      await remindInvoice.mutateAsync({ invoiceId: invoice.id, ...invoiceWorkOrderRef(invoice.bookingId) });
      notify.success(t('actions.remindSuccess'));
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  const handleRetryDelivery = async (deliveryId: string) => {
    try {
      await retryDelivery.mutateAsync({ invoiceId: invoice.id, deliveryId });
      notify.success(t('delivery.retryQueued'));
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  const handleFinalize = async () => {
    if (!canFinalize) return;
    try {
      await finalize.mutateAsync({
        invoiceId: invoice.id,
        ...invoiceWorkOrderRef(invoice.bookingId),
        // The channel decides what the recipient must have: a company that
        // takes e-invoices needs no email address (#3071).
        channel: einvoice.preferred ? 'einvoice' : 'email',
      });
      notify.success(t('actions.finalizeSuccess'));
      setFinalizeDialogOpen(false);
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) return;
    try {
      await voidInvoice.mutateAsync({
        invoiceId: invoice.id,
        reason: voidReason,
        ...invoiceWorkOrderRef(invoice.bookingId),
      });
      notify.success(t('actions.voidSuccess'));
      setVoidDialogOpen(false);
      setVoidReason('');
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/invoices')}
        sx={{ mb: 2 }}
        size="small"
      >
        {t('detailPage.backToList')}
      </Button>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {invoice.invoiceNumber}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={t(`status.${invoice.status}`)}
                color={STATUS_COLORS[invoice.status] ?? 'default'}
              />
              {isOverdue ? (
                <Chip size="small" color="error" label={t('detail.overdue')} />
              ) : null}
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                {[invoice.customerName, invoice.serviceType, invoice.vehicleReg]
                  .filter((part) => part.trim())
                  .join(' · ')}
              </Typography>
            </Stack>
            {invoice.bookingId ? (
              <Link
                component={RouterLink}
                to={`/workorders/${invoice.bookingId}`}
                variant="caption"
                sx={{ display: 'inline-block', mt: 1 }}
              >
                {t('detailPage.viewWorkOrder')}
              </Link>
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'inline-block', mt: 1 }}
                data-testid="invoice-no-work-order"
              >
                {t('detailPage.noWorkOrder')}
              </Typography>
            )}
          </Box>
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant="body2" color="text.secondary">
              {t('detail.invoiceDate', { defaultValue: t('print.invoiceDate') })}
            </Typography>
            <Typography variant="body1">
              {formatDate(invoice.issueDate)}
            </Typography>
            {invoice.dueDate ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('print.dueDate')}
                </Typography>
                <Typography variant="body1" color={isOverdue ? 'error.main' : 'text.primary'}>
                  {formatDate(invoice.dueDate)}
                </Typography>
              </>
            ) : null}
          </Box>
        </Stack>
      </Paper>

      {/* Void banner */}
      {invoice.status === 'void' && (
        <Alert
          severity="error"
          variant="outlined"
          data-testid="invoice-detail-void-banner"
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            {t('print.voidBanner')}
          </Typography>
          {invoice.voidedAt ? (
            <Typography variant="caption" sx={{ display: 'block' }}>
              {t('print.voidedAt')}: {formatDateTime(invoice.voidedAt)}
            </Typography>
          ) : null}
          {invoice.voidReason ? (
            <Typography variant="caption" sx={{ display: 'block' }}>
              {t('print.voidReason')}: {invoice.voidReason}
            </Typography>
          ) : null}
        </Alert>
      )}

      {/* Invoice content is immutable once created (#1960); drafts are read-only too (#3876). */}
      {invoice.status === 'draft' && canManageInvoices ? (
        <Stack spacing={2} sx={{ mb: 3 }} data-testid="draft-invoice-readonly-info">
          <Alert severity="info">{t('detailPage.draftReadOnly')}</Alert>
          {finalizeBlockedReason ? (
            <Alert severity="warning" data-testid="draft-invoice-finalize-blocked">
              {finalizeBlockedReason}
            </Alert>
          ) : null}
        </Stack>
      ) : null}

      <InvoiceConsolidationSection
        invoiceId={invoice.id}
        status={invoice.status}
        canManage={canManageInvoices}
        presentation={consolidationView}
        isError={consolidationQuery.isError}
      />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('print.description')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('print.description')}</TableCell>
                <TableCell align="center">{t('print.quantity')}</TableCell>
                <TableCell align="right">{t('edit.unitPriceInclVat')}</TableCell>
                <TableCell align="right">{t('print.lineDiscountHeader')}</TableCell>
                <TableCell align="right">{t('print.lineVat')}</TableCell>
                <TableCell align="right">{t('edit.lineTotalInclVat')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.lineItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                    {t('detailPage.noLineItems')}
                  </TableCell>
                </TableRow>
              ) : (
                lineGroups.map((group) => {
                  const groupNote = group.lines
                    .map((li) => li.groupNotes?.trim())
                    .find((note): note is string => Boolean(note && note.length > 0));
                  return (
                  <Fragment key={group.key}>
                    <TableRow data-testid="invoice-line-group-row">
                      <TableCell colSpan={6} sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>
                        {group.name}
                        {groupNote ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 400 }}
                            data-testid="invoice-line-group-notes"
                            aria-label={t('print.groupNotes')}
                          >
                            {groupNote}
                          </Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    {group.lines.map((li) => (
                      <TableRow key={li.id} data-testid="invoice-line-row">
                        <TableCell>{li.description}</TableCell>
                        <TableCell align="center">
                          {formatQuantity(li.quantity)} {tc(`units.${li.unit || 'piece'}`)}
                        </TableCell>
                        <TableCell align="right">
                          {formatPrice(displayGrossCents(li.unitPriceCents, li.taxable, li.taxPercent, invoice.taxPercent))}
                        </TableCell>
                        <TableCell align="right">
                          {li.discountPercent > 0 ? `${formatPercent(li.discountPercent)} %` : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {li.taxable ? `${formatPercent(li.taxPercent ?? invoice.taxPercent)} %` : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {formatPrice(displayLineGrossCents(li, invoice.taxPercent))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />

        {/* Totals */}
        <Box sx={{ ml: 'auto', maxWidth: 360 }}>
          <Row label={t('detail.subtotal')} value={formatPrice(invoice.subtotalCents)} />
          {invoice.taxBreakdown && invoice.taxBreakdown.length > 0 ? (
            invoice.taxBreakdown
              .filter((group) => group.taxPercent > 0)
              .map((group) => (
                <Row
                  key={group.taxPercent}
                  label={t('detail.tax', { percent: formatPercent(group.taxPercent) })}
                  value={formatPrice(group.taxCents)}
                />
              ))
          ) : (
            <Row label={t('detail.tax', { percent: formatPercent(invoice.taxPercent) })} value={formatPrice(invoice.taxCents)} />
          )}
          {invoice.discountCents > 0 && (
            <Row label={t('detail.discount')} value={`-${formatPrice(invoice.discountCents)}`} color="error.main" />
          )}
          <Divider sx={{ my: 1 }} />
          <Row label={t('detail.total')} value={formatPrice(invoice.totalCents)} bold />
          {invoice.paidAmountCents > 0 && (
            <Row label={t('detail.paidAmount')} value={formatPrice(invoice.paidAmountCents)} color="success.main" />
          )}
          {remainingCents > 0 && invoice.status !== 'void' && (
            <Row label={t('detail.remaining')} value={formatPrice(remainingCents)} bold color="error.main" />
          )}
        </Box>
      </Paper>

      {/* Payment history */}
      {invoice.status !== 'void' && (invoice.payments.length > 0 || remainingCents > 0) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <PaymentHistorySection payments={invoice.payments} remainingCents={remainingCents} />
        </Paper>
      )}

      {canManageInvoices ? <SveaPaymentSection invoice={invoice} canManage={canManageInvoices} /> : null}

      <SveaRefundControls
        invoice={invoice}
        canManage={canManageInvoices}
        {...(settlement.data?.payments ? { sveaPayments: settlement.data.payments } : {})}
      />

      {canReadSettlements && settlement.data && settlement.data.payments.length > 0 ? (
        <Paper sx={{ p: 3, mb: 3 }} data-testid="svea-invoice-settlement">
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            {t('sveaSettlements.invoiceTitle')}
          </Typography>
          <Stack spacing={1}>
            <Chip
              size="small"
              color={settlement.data.settled ? 'success' : 'warning'}
              label={
                settlement.data.settled ? t('sveaSettlements.settled') : t('sveaSettlements.notSettled')
              }
              data-testid="svea-invoice-settlement-status"
            />
            {settlement.data.settlementDate ? (
              <Typography variant="body2" data-testid="svea-invoice-settlement-date">
                {t('sveaSettlements.settlementDate')}:{' '}
                {formatDate(settlement.data.settlementDate)}
              </Typography>
            ) : null}
            <Typography variant="body2" data-testid="svea-invoice-settlement-commission">
              {t('sveaSettlements.commissionFee')}: {formatPrice(settlement.data.commissionCents)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('sveaSettlements.commissionHint')}
            </Typography>
          </Stack>
        </Paper>
      ) : null}

      {/* Delivery audit (finalized invoices) */}
      {isFinalized && (invoice.deliveries.length > 0 || receiptIds.length > 0) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <InvoiceDeliverySection
            invoiceId={invoice.id}
            receiptIds={receiptIds}
            deliveries={invoice.deliveries}
            canManage={canManageInvoices}
            onRetry={(deliveryId) => void handleRetryDelivery(deliveryId)}
            retrying={retryDelivery.isPending}
            onSendAsEmail={() => void handleDeliver('delivery.sendAsEmailQueued')}
            sendingAsEmail={deliverInvoice.isPending}
          />
        </Paper>
      )}

      {/* Credit notes + refunds (#1037) */}
      {(invoice.creditNotes.length > 0 || invoice.refunds.length > 0) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          {invoice.creditNotes.length > 0 && (
            <Box sx={{ mb: invoice.refunds.length > 0 ? 2 : 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {t('credit.listTitle')}
              </Typography>
              {invoice.creditNotes.map((c) => (
                <Box key={c.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{c.invoiceNumber}</Typography>
                  <Typography variant="body2" color="error.main">{formatPrice(c.totalCents)}</Typography>
                </Box>
              ))}
            </Box>
          )}
          {invoice.refunds.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {t('refund.listTitle')}
              </Typography>
              {invoice.refunds.map((r) => {
                const completed = isCompletedCustomerRefundStatus(r.status);
                return (
                <Box
                  key={r.id}
                  sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, gap: 1, alignItems: 'center' }}
                  data-testid="invoice-ledger-refund"
                  data-refund-status={r.status}
                  data-completed-customer-refund={completed ? 'true' : 'false'}
                >
                  <Typography variant="body2">
                    {formatDate(r.refundedAt)}
                    {r.reason ? ` · ${r.reason}` : ''}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      color={completed ? 'default' : 'warning'}
                      label={t(`refund.ledgerStatus.${r.status}`, { defaultValue: r.status })}
                    />
                    <Typography variant="body2" color={completed ? 'error.main' : 'text.secondary'}>
                      -{formatPrice(r.amountCents)}
                    </Typography>
                  </Stack>
                </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      )}

      {/* References, notes and terms */}
      {invoice.customerReference || invoice.paymentReferenceNumber || invoice.notes || invoice.termsAndConditions ? (
        <Paper sx={{ p: 3, mb: 3 }} data-testid="invoice-detail-extras">
          <Stack spacing={1.5}>
            {invoice.customerReference ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('edit.customerReference')}
                </Typography>
                <Typography variant="body2">{invoice.customerReference}</Typography>
              </Box>
            ) : null}
            {invoice.paymentReferenceNumber ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('edit.paymentReferenceNumber')}
                </Typography>
                <Typography variant="body2">{invoice.paymentReferenceNumber}</Typography>
              </Box>
            ) : null}
            {invoice.notes ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('detailPage.notes')}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {invoice.notes}
                </Typography>
              </Box>
            ) : null}
            {invoice.termsAndConditions ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('generate.terms')}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {invoice.termsAndConditions}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      {/* Actions */}
      {invoice.status !== 'void' && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {isFinalized ? (
            <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => void handleOpenPdf()}>
              {t('actions.downloadPdf')}
            </Button>
          ) : (
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
              {t('actions.print')}
            </Button>
          )}
          {canManageInvoices && isFinalized && (
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={() => void handleDeliver()}
              disabled={deliverInvoice.isPending}
            >
              {t('actions.deliver')}
            </Button>
          )}
          {canManageInvoices && isFinalized && einvoiceable && einvoice.visible && (
            <Tooltip
              title={einvoice.available ? '' : t(invoice.billingParty?.companyId ? 'generate.einvoiceNoAddress' : 'generate.einvoiceNoCompany')}
              disableHoverListener={einvoice.available}
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<OutboxIcon />}
                  onClick={() => void handleDeliverEinvoice()}
                  disabled={deliverInvoice.isPending || !einvoice.available}
                  data-testid="invoice-deliver-einvoice"
                >
                  {t('actions.deliverEinvoice')}
                </Button>
              </span>
            </Tooltip>
          )}
          {canManageInvoices && isOverdue && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<EmailIcon />}
              onClick={() => void handleRemind()}
              disabled={remindInvoice.isPending}
            >
              {t('actions.remind')}
            </Button>
          )}
          {canManageInvoices && invoice.status === 'draft' && (
            <Tooltip
              title={finalizeBlockedReason ?? ''}
              disableHoverListener={canFinalize}
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => setFinalizeDialogOpen(true)}
                  disabled={finalize.isPending || !canFinalize}
                  data-testid="draft-invoice-finalize"
                >
                  {t('actions.finalize')}
                </Button>
              </span>
            </Tooltip>
          )}
          {canTakePayments && invoice.status !== 'paid' && !collecting && (
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => setPaymentDialogOpen(true)}
            >
              {t('actions.recordPayment')}
            </Button>
          )}
          {canManageInvoices && canCredit && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ReceiptLongIcon />}
              onClick={() => setCreditDialogOpen(true)}
            >
              {t('actions.creditNote')}
            </Button>
          )}
          {canManageInvoices && !collecting && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<BlockIcon />}
              onClick={() => setVoidDialogOpen(true)}
            >
              {t('actions.void')}
            </Button>
          )}
        </Stack>
      )}

      <RecordPaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoiceNumber}
        totalCents={invoice.totalCents}
        paidAmountCents={invoice.paidAmountCents}
        customerEmail={invoice.customerEmail}
        initialMethod={recordPaymentInitialMethod(invoice.status)}
        {...invoiceWorkOrderRef(invoice.bookingId)}
      />

      <ConfirmDialog
        open={voidDialogOpen}
        title={t('actions.voidTitle')}
        message={[
          t('actions.voidConfirm', { invoiceNumber: invoice.invoiceNumber }),
          // A delivered invoice cannot be voided server-side (#1960); say so
          // here instead of after the click (#3115).
          invoice.deliveries.some((delivery) => delivery.status === 'sent') ? t('actions.voidDeliveredHint') : null,
        ].filter(Boolean).join(' ')}
        confirmText={t('actions.void')}
        confirmColor="error"
        onConfirm={() => void handleVoid()}
        onCancel={() => {
          setVoidDialogOpen(false);
          setVoidReason('');
        }}
        loading={voidInvoice.isPending}
      />

      <ConfirmDialog
        open={finalizeDialogOpen}
        title={t('actions.finalizeTitle')}
        message={t('actions.finalizeConfirm', { invoiceNumber: invoice.invoiceNumber })}
        confirmText={t('actions.finalize')}
        onConfirm={() => void handleFinalize()}
        onCancel={() => setFinalizeDialogOpen(false)}
        loading={finalize.isPending}
      />

      {canCredit && creditDialogOpen && (
        <CreditNoteDialog
          open={creditDialogOpen}
          onClose={() => setCreditDialogOpen(false)}
          invoice={invoice}
        />
      )}

    </Box>
  );
}

function Row({
  label,
  value,
  bold = false,
  color,
}: {
  label: string;
  value: string;
  bold?: boolean;
  color?: string;
}) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" fontWeight={bold ? 700 : 400} {...(color ? { color } : {})}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 500} {...(color ? { color } : {})}>
        {value}
      </Typography>
    </Box>
  );
}
